import { Getter } from '../Getter';
import { HttpError } from '../HttpError';
import { ApiSnippet } from './Models/ApiSnippet';
import isValidUUID from 'uuid-validate';
import URI from 'urijs';

type ApiResponse = ApiSnippet | null | undefined;

interface DataResponse {
  data?: string;
  error?: never;
}
interface ErrorResponse {
  data?: never;
  error: HttpError;
}
export type ClientOutput = DataResponse | ErrorResponse;

interface ClientInput {
  commentUUID: string;
  contextLinesBefore?: number;
  contextLinesAfter?: number;
}

export class SnippetsClient {
  private getter: Getter<ApiResponse | HttpError>;

  constructor(getter: Getter<ApiResponse | HttpError>) {
    this.getter = getter;
  }

  public async retrieveAsync(input: ClientInput): Promise<ClientOutput> {
    const { commentUUID } = input;

    if (!isValidUUID(commentUUID)) {
      // equivalent to the response we'd get on the server
      return {
        error: new HttpError(400),
      };
    }

    const response = await this.tryRetrieveAsync(input);

    if (!response) {
      return {
        data: undefined,
        error: undefined,
      };
    }

    if (response instanceof HttpError) {
      return {
        error: response,
      };
    }

    return {
      data: response.snippet,
    };
  }

  private async tryRetrieveAsync(
    input: ClientInput
  ): Promise<ApiResponse | HttpError> {
    const href = this.getHref(input);

    return await this.getter.get(href);
  }

  public getHref({
    commentUUID,
    contextLinesBefore,
    contextLinesAfter,
  }: ClientInput): string {
    const href = URI('/api/v1/comments/')
      .segmentCoded(commentUUID)
      .segment('snippet');

    if (contextLinesBefore) {
      href.addQuery({ context_lines_before: contextLinesBefore });
    }

    if (contextLinesAfter) {
      href.addQuery({ context_lines_after: contextLinesAfter });
    }

    return href.toString();
  }
}
